import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { FaMagic } from "react-icons/fa";
import { IoIosArrowBack, IoIosArrowDropdown, IoMdClose } from "react-icons/io";
import { AiOutlineLike } from "react-icons/ai";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  containerVariants,
  itemVariants,
  dropdownVariants,
  buttonAnimations,
} from "../../utils/Animations";
import TableLoader from "../../utils/TableLoader";
import { RiArrowDownSLine } from "react-icons/ri";
import { useTranslation } from 'react-i18next';

const MenuProduct = (props) => {
  // Hooks and initial state
  const { menuData, isLoading } = props;
  const navigate = useNavigate();
  const logoUrl = import.meta.env.VITE_BACKEND_APP_STORAGE_URL;
  const apiUrl = import.meta.env.VITE_METERI_API_BASE_URL;
  const token = localStorage.getItem("token");
  const rest_id = localStorage.getItem("currentRestId");
  const { t } = useTranslation();

  // State management
  const [userType, setUserType] = useState("");
  const [expandedItems, setExpandedItems] = useState({});
  const [dishId, setDishId] = useState("");
  const [optionsData, setOptionsData] = useState(null);
  const [openSection, setOpenSection] = useState(null);
  const [pairModal, setPairModal] = useState(false);
  const [dishName, setDishName] = useState("");
  const [courses, setCourses] = useState([{ name: t('menuProductPage.dropDownAll') }]);
  const [selectedCourse, setSelectedCourse] = useState(t('menuProductPage.dropDownAll'));
  const [toggleCourse, setToggleCourse] = useState(false);


  const dishData = menuData?.dishes_dishCourse;

  // Update selectedCourse when language changes
  useEffect(() => {
    setSelectedCourse(t('menuProductPage.dropDownAll'));
  }, [t]);

  // Initialize user role
  useEffect(() => {
    const role = localStorage.getItem("Role");
    setUserType(role);
  }, []);

  // Memoized filtered values
  const filteredDishData = useMemo(() => {
    return selectedCourse === t('menuProductPage.dropDownAll')
      ? dishData
      : dishData.filter(item => item.name === selectedCourse);
  }, [selectedCourse, dishData, t]);

  const dropdownCourses = useMemo(() => {
    const displayedTitles = new Set(
      dishData
        .filter(item => item?.dishes?.length)
        .map(item => item.name)
    );
    return courses.filter(
      course => course?.name === t('menuProductPage.dropDownAll') || displayedTitles.has(course?.name)
    );
  }, [courses, dishData, t]);

  // Constants
  const options = useMemo(() => [
    {
      label: "Abbinamento Perfetto",
      icon: (
        <>
          <AiOutlineLike fontSize={23} color="black" gap={0} />
          <AiOutlineLike fontSize={23} color="black" />
          <AiOutlineLike fontSize={23} color="black" />
        </>
      ),
    },
    {
      label: "Buon Abbinamento",
      icon: <AiOutlineLike fontSize={23} color="black" />,
    },
    {
      label: "Abbinamento Creativo",
      icon: <FaMagic fontSize={23} color="black" />,
    },
  ], []);

  // Handlers
  const toggleDescription = useCallback((index) => {
    setExpandedItems(prev => ({ ...prev, [index]: !prev[index] }));
  }, []);

  const toggleSection = useCallback((sectionLabel) => {
    setOpenSection(prev => prev === sectionLabel ? null : sectionLabel);
  }, []);

  const handleOpenModal = useCallback((id) => {
    setPairModal(true);
    setDishId(id);
  }, []);

  const closeModal = useCallback(() => {
    setPairModal(false);
  }, []);

  const handleBackgroundClick = useCallback((e) => {
    if (e.target.id === "modal-background") {
      setPairModal(false);
    }
  }, []);

  // Data fetching
  const pairingWinesData = useCallback(async () => {
    if (!dishId) return;

    try {
      const response = await axios.get(
        `${apiUrl}/api/v1/dishes/${dishId}/pairedWines`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOptionsData(response?.data);
    } catch (error) {
      console.error("Failed to fetch wine data:", error);
      setOptionsData(error?.response?.data || null);
    }
  }, [apiUrl, dishId, token]);

  // Effects
  useEffect(() => {
    if (pairModal && dishId) {
      pairingWinesData();
    } else {
      setOptionsData(null);
    }
  }, [pairModal, dishId, pairingWinesData]);

  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        const response = await axios.get(
          `${apiUrl}/api/v1/restaurant/${rest_id}/dish-courses`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { pagination: 0 },
          }
        );
        setCourses([{ name: t('menuProductPage.dropDownAll') }, ...(response?.data?.data || [])]);
      } catch (error) {
        console.error("Error fetching course", error);
      }
    };

    fetchCourseData();
  }, [apiUrl, rest_id, token, t]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!event.target.closest(".dropdown-container")) {
        setToggleCourse(false);
      }
    };

    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, []);

  return (
    <motion.div
      initial={containerVariants.initial}
      animate={containerVariants.animate}
      transition={containerVariants.transition}
      className="w-full flex items-center justify-center p-5 py-1 mt-[20px]"
    >
      <motion.div
        className="w-[90%]"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        {/* Title */}
        <motion.h1
          className="poppins-medium w-full text-center sm:text-[42px] text-[35px] sm:my-5 my-3 text-[#8B2433] uppercase"
          initial={itemVariants.initial}
          animate={itemVariants.animate}
          transition={itemVariants.transition}
        >
          {menuData?.menu?.name || "I Nostri Menu"}
        </motion.h1>

        <div className="w-full flex flex-col">

          <div className="w-full flex flex-col lg:flex-row justify-end gap-2 mt-5">
            <div className="text-xl  flex items-center justify-between poppins-medium">
              <label className="w-auto text-end text-[22px] leading-[27px] text-2xl text-[#8B2433]  block">
                Portata:
              </label>
            </div>
            <div className="dropdown-main flex flex-col md:flex-row   items-start md:items-center gap-3">
              <motion.div className="relative w-[250px] bg-[#FFFFFF] dropdown-container rounded-[28px]">
                <motion.button
                  className={`${toggleCourse
                    ? "border-b-0 rounded-tl-3xl rounded-tr-3xl"
                    : "rounded-full"
                    } border bg-[#fff] font-medium border-[#d5d6d9] inline-flex justify-between items-center w-full px-4 py-[8px] outline-none text-[#6B6B6B] shadow-sm relative z-30 text-sm`}
                  onClick={() => setToggleCourse(!toggleCourse)}
                  {...buttonAnimations}
                >
                  <span className="mr-2 flex items-center text-[#8b2433] gap-3 poppins-medium">
                    {selectedCourse}
                  </span>
                  <RiArrowDownSLine
                    className={`transform transition-transform duration-300 ${toggleCourse ? "rotate-180" : ""
                      }`}
                    size="24px"
                    color="#9f414f"
                  />
                </motion.button>
                <AnimatePresence>
                  {toggleCourse && (
                    <motion.div
                      className="bg-[#FFFFFF] opacity-100 z-50 border border-[#d5d6d9] absolute pt-[9px] rounded-t-none border-t-0 p-2 px-4 right-0 mt-0 rounded-[21px] shadow-lg text-[#DB7685] space-y-1 w-full"
                      variants={dropdownVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      {dropdownCourses
                        .filter((course) => course?.name !== selectedCourse)
                        .map((course, index) => (
                          <motion.label
                            key={course?.name}
                            className={`px-0 py-2 cursor-pointer flex justify-between flex-row gap-[15px] filter_list_btn ${index !== dropdownCourses.length - 1
                              ? "border-b border-[#8b24331f]"
                              : ""
                              } group hover:border-[#8b2433]`}
                            onClick={() => {
                              setSelectedCourse(course?.name);
                              setToggleCourse(false);
                            }}
                          >
                            <span className="text-xs text-[#8B8B8B] rounded-md flex items-center poppins-medium group-hover:text-[#8b2433]">
                              {course?.name}
                            </span>
                          </motion.label>
                        ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          </div>
        </div>


        {/* Course Dropdown */}
        <motion.div
          className="flex justify-end w-full mt-[50px]"
          variants={itemVariants}
        >
          <div className="dropdown-main flex w-[50%] justify-end items-center gap-3"></div>
        </motion.div>

        {/* Dish List */}
        <AnimatePresence>
          {isLoading?.main ? (
            <div className="flex my-20 justify-center items-center h-full">
              <TableLoader />
            </div>
          ) : (
            <>
              {(() => {
                const displayedTitlesSet = new Set();
                if (!filteredDishData?.length) {
                  return (
                    <motion.div
                      className="text-center py-10"
                      initial={itemVariants.initial}
                      animate={itemVariants.animate}
                      exit={itemVariants.exit}
                    >
                      Nessun piatto trovato
                    </motion.div>
                  );
                }
                return filteredDishData?.map((dish, index) => {
                  if (!dish?.dishes?.length) return null;
                  const title = dish?.name;
                  return (
                    <motion.div
                      key={index}
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      className="space-y-6"
                    >
                      {/* Render title only once per category */}
                      {!displayedTitlesSet.has(title) && (
                        <>
                          <motion.div
                            className="bg-transparent p-0 xl:p-3 mt-5 mx-3 text-left max-w-fit poppins-medium rounded-lg"
                            variants={itemVariants}
                            initial="hidden"
                            animate="visible"
                          >
                            <h2 className="text-2xl text-[#8B2433] m-0">
                              {title}
                            </h2>
                          </motion.div>
                          <motion.div
                            className="h-[3px] w-full bg-[#8b2433] lg:mx-auto my-1 lg:my-2"
                            variants={itemVariants}
                            initial="hidden"
                            animate="visible"
                          />
                        </>
                      )}
                      <div className="p-1 grid grid-cols-1 sm:grid-cols-2  lg:grid-cols-1 xl:grid-cols-1 gap-[2.3%] gap-y-3">
                        {dish?.dishes?.map((dishes, dishIndex) => (
                          <motion.div
                            key={dishIndex}
                            className="rounded-3xl relative w-[100%] bg-[#f8f1f1] p-4 pt-8 pb-6 z-50 lg:max-w-[100%] xl:max-w-[85%] 2xl:max-w-[75%] xl:m-auto "
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                          >
                            <div className="list-parent hidden lg:block">
                              <ul className="flex sm:flex-row flex-col flex-wrap items-center mb-[8px] justify-start lg:justify-end  dish-price-desktop">
                                {dishes?.pivot?.is_option_to_be_full_dish ===
                                  1 && (
                                    <li
                                      className={`w-full sm:w-auto self-end flex items-center justify-start sm:justify-end gap-x-[10px] sm:pr-[10px] ${dishes?.pivot
                                        ?.is_option_to_be_half_dish === 1
                                        ? "sm:border-r"
                                        : ""
                                        } border-[#000000]`}
                                    >
                                      {/* <span className="text-[17px] leading-[26px] text-[#6B6B6B] block"> Full Price:</span> */}
                                      <span className="spinja-font text-[#8B2433] text-[18px] xl:text-[20px] leading-[26px] font-bold block">
                                        {dishes?.pivot?.full_price
                                          ? dishes?.pivot?.full_price
                                          : "N/A"}{" "}
                                        €
                                      </span>
                                    </li>
                                  )}
                                {dishes?.pivot?.is_option_to_be_half_dish ===
                                  1 && (
                                    <li className="w-full sm:w-auto  sm:pl-[10px] flex items-center self-end justify-start gap-x-[10px]">
                                      <span className="text-[17px] leading-[26px] text-[#6B6B6B] block">
                                        Mezza Porzione:
                                      </span>
                                      <span className="spinja-font text-[#8B2433] text-[18px] xl:text-[20px] leading-[26px] font-bold block">
                                        {dishes?.pivot?.half_price
                                          ? dishes?.pivot?.half_price
                                          : "N/A"}{" "}
                                        €
                                      </span>
                                    </li>
                                  )}
                              </ul>
                            </div>
                            <div className="gap-4 flex-col flex mt-2 justify-between xl:flex-row">
                              {dishes?.picture && (
                                <motion.div
                                  className="flex "
                                  variants={itemVariants}
                                >
                                  <div className="flex rounded-[20px] justify-center  w-[100px] h-[100px]  xl:w-[110px] xl:h-[110px]  2xl:w-[250px] 2xl:h-[250px] xl:m-auto cursor-pointer">
                                    <img
                                      className="rounded-[50%]"
                                      src={`${logoUrl}/${dishes?.picture}`}
                                      alt={dishes?.name}
                                    />
                                  </div>
                                </motion.div>
                              )}
                              <motion.div
                                className="max-w-[1100px] w-[100%]"
                                variants={itemVariants}
                              >
                                <div className="pricefull half-price">
                                  <div className="list-parent  block lg:hidden ">
                                    <ul className="flex sm:flex-row flex-col flex-wrap items-center mb-[8px] justify-start lg:justify-end  dish-price-desktop">
                                      {dishes?.pivot
                                        ?.is_option_to_be_full_dish === 1 && (
                                          <li
                                            className={`w-full sm:w-auto self-end flex items-center justify-start sm:justify-end gap-x-[10px] sm:pr-[10px] ${dishes?.pivot
                                              ?.is_option_to_be_half_dish === 1
                                              ? "lg:border-r"
                                              : ""
                                              } border-[#000000]`}
                                          >
                                            {/* <span className="text-[17px] leading-[26px] text-[#6B6B6B] block"> Full Price:</span> */}
                                            <span className="spinja-font text-[#8B2433] text-[18px] xl:text-[20px] leading-[26px] font-bold block">
                                              {dishes?.pivot?.full_price
                                                ? dishes?.pivot?.full_price
                                                : "N/A"}{" "}
                                              €
                                            </span>
                                          </li>
                                        )}
                                      {dishes?.pivot
                                        ?.is_option_to_be_half_dish === 1 && (
                                          <li className="w-full sm:w-auto  md:pl-[10px] flex items-center self-end justify-start gap-x-[10px]">
                                            <span className="text-[17px] leading-[26px] text-[#6B6B6B] block">
                                              Mezza Porzione:
                                            </span>
                                            <span className="spinja-font text-[#8B2433] text-[18px] xl:text-[20px] leading-[26px] font-bold block">
                                              {dishes?.pivot?.half_price
                                                ? dishes?.pivot?.half_price
                                                : "N/A"}{" "}
                                              €
                                            </span>
                                          </li>
                                        )}
                                    </ul>
                                  </div>
                                  <div className="flex-1">
                                    <p className="spinja-font mt-4 mb-[5px] text-[21px] leading-[26px] text-[#132F40] font-bold">
                                      {dishes?.name ? dishes?.name : "N/A"}
                                    </p>
                                    <p className="text-[17px] mb-2 mt-2 leading-[26px] text-[#6B6B6B]">
                                      {dishes?.allergens?.length
                                        ? dishes.allergens
                                          .map(
                                            (allergen) => allergen?.name_it
                                          )
                                          .join(", ")
                                        : ""}
                                    </p>
                                    {dishes?.description && (
                                      <div
                                        className={`w-full poppins-light-italic overflow-hidden text-[#505050] text-xs transition-all duration-1000 ease-in-out ${expandedItems[index]
                                          ? "max-h-[1000px] duration-1000"
                                          : "max-h-8 duration-500"
                                          }`}
                                      >
                                        <div
                                          dangerouslySetInnerHTML={{
                                            __html: dishes?.description,
                                          }}
                                        />
                                      </div>
                                    )}
                                    <ul className="flex sm:flex-row flex-col mt-3 gap-2 lg:gap-4 items-center mb-[8px] justify-start sm:justify-end dish-price-mobile">
                                      {dishes?.pivot
                                        ?.is_option_to_be_full_dish === 1 && (
                                          <li
                                            className={` w-full sm:w-auto self-end flex items-center justify-start sm:justify-end gap-x-[10px] sm:pr-[20px] ${dishes?.pivot
                                              ?.is_option_to_be_half_dish === 1
                                              ? "sm:border-r"
                                              : ""
                                              } border-[#000000]`}
                                          >
                                            {/* <span className="text-[17px] leading-[26px] text-[#6B6B6B] block"> Full Price:</span> */}
                                            <span className="spinja-font text-[#8B2433] text-[20px] leading-[26px] font-bold block">
                                              {dishes?.pivot?.full_price
                                                ? dishes?.pivot?.full_price
                                                : "N/A"}{" "}
                                              €
                                            </span>
                                          </li>
                                        )}
                                      {dishes?.pivot
                                        ?.is_option_to_be_half_dish === 1 && (
                                          <li className="w-full sm:w-auto  sm:pl-[20px] flex items-center self-end justify-start gap-x-[10px]">
                                            <span className="text-[17px] leading-[26px] text-[#6B6B6B] block">
                                              Mezza Porzione:
                                            </span>
                                            <span className="spinja-font text-[#8B2433] text-[20px] leading-[26px] font-bold block">
                                              {dishes?.pivot?.half_price
                                                ? dishes?.pivot?.half_price
                                                : "N/A"}{" "}
                                              €
                                            </span>
                                          </li>
                                        )}
                                    </ul>
                                    <div className="pt-[20px] xl:pt-[41px]  flex xl:flex-row items-center justify-between xl:items-end sm:items-start sm:gap-[20px]">
                                      {dishes?.description &&
                                        dishes.description.length > 300 && (
                                          <button
                                            className="block font-medium text-[15px] leading-[23px] text-[#8B2433] uppercase italic py-[13px] px-[30px] border border-[#8B2433] rounded-[28px]"
                                            onClick={() =>
                                              toggleDescription(index)
                                            }
                                          >
                                            {expandedItems[index]
                                              ? "MOSTRA MENO"
                                              : "SCOPRI DI PIÙ"}
                                          </button>
                                        )}
                                      {dishes?.wine_pairings_count > 0 && (
                                        <div className="flex items-center justify-center ml-auto">
                                          <p
                                            className="text-[17px] leading-[26px] text-[#6B6B6B] cursor-pointer hover:underline"
                                            onClick={() => {
                                              handleOpenModal(dishes?.id);
                                              setDishName(dishes?.name);
                                            }}
                                          >
                                            {menuData?.menu?.restaurant
                                              ?.show_more ||
                                              "Che vino ci abbino?"}
                                          </p>
                                        </div>
                                      )}

                                      {pairModal && (
                                        <div
                                          id="modal-background"
                                          className="fixed inset-0 z-[999] flex items-center justify-center w-full h-full bg-transparent bg-opacity-50 backdrop-blur-sm"
                                          onClick={handleBackgroundClick}
                                        >
                                          <div className="bg-[#8B2433] rounded-2xl shadow-xl p-6 w-[80%] sm:w-[90%] relative ">
                                            <button
                                              onClick={closeModal}
                                              className="absolute sm:top-[38px] top-[13px] sm:right-[25px] right-[20px] text-[#fff] hover:text-gray-900 text-2xl"
                                            >
                                              <IoMdClose />
                                            </button>
                                            <h2 className="text-2xl font-semibold text-[#fff] sm:mb-4 mb-2 sm:text-center">
                                              {dishName} Wine Pair
                                            </h2>

                                            <div className="sm:pt-[40px] pt-[20px]">
                                              {(() => {
                                                // Filter sections to only those that have wine pairings
                                                const validSections =
                                                  options?.filter(
                                                    (section) =>
                                                      Array.isArray(
                                                        optionsData
                                                      ) &&
                                                      optionsData.some(
                                                        (item) =>
                                                          item?.pairing_level ===
                                                          section?.label
                                                      )
                                                  );

                                                // If no sections have pairings, show message
                                                if (
                                                  validSections?.length === 0
                                                ) {
                                                  return (
                                                    <div className="text-center py-10">
                                                      <p className="text-gray-100 poppins-medium text-lg">
                                                        No wine pairing records
                                                        found
                                                      </p>
                                                    </div>
                                                  );
                                                }

                                                // Otherwise render the valid sections
                                                return validSections?.map(
                                                  (section) => (
                                                    <div
                                                      key={section?.label}
                                                      className="mb-4"
                                                    >
                                                      <button
                                                        onClick={() =>
                                                          toggleSection(
                                                            section?.label
                                                          )
                                                        }
                                                        className="w-full text-left flex item-center items-center font-medium justify-between text-lg py-3 px-4 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                                                      >
                                                        <p className="flex items-center gap-x-1 sm:text-[18px] text-[15px]">
                                                          {section?.label}{" "}
                                                          {section?.icon}
                                                        </p>
                                                        <IoIosArrowDropdown
                                                          className={`transform transition-transform duration-200 ${openSection === section?.label
                                                            ? "rotate-180"
                                                            : "rotate-0"
                                                            }`}
                                                        />
                                                      </button>
                                                      {openSection === section?.label && (
                                                        <div className="mt-2 space-y-2 transition-all duration-300 max-h-[250px] overflow-y-auto popup-wine">
                                                          <div className="p-1 grid xl:grid-cols-3 lg:grid-cols-2 grid-cols-1 gap-[2.3%] gap-y-5">
                                                            {optionsData
                                                              ?.filter((item) => item?.pairing_level === section?.label)
                                                              ?.map(
                                                                (item, index) => (
                                                                  <div
                                                                    key={index}
                                                                    className="relative w-[100%] gap-4 flex-col md:flex-row flex mt-5 bg-[#F6F6F6] justify-between p-4 rounded-[10px] pt-8 pb-6"
                                                                  >
                                                                    <div className="flex justify-center max-w-[150px] m-auto">
                                                                      {item?.wine_stock?.wine?.image && (
                                                                        <img
                                                                          src={item?.wine_stock?.wine?.wines_from === "restaurant"
                                                                            ? `${logoUrl}/${item?.wine_stock?.wine?.image}`
                                                                            : item?.wine_stock?.wine?.image
                                                                          }
                                                                          className="relative md:h-[150px] h-full w-[100%] bg-cover object-cover"
                                                                          alt="wineBottle"
                                                                        />
                                                                      )}
                                                                    </div>

                                                                    <div className="flex md:flex-row flex-col-reverse justify-between w-[100%] gap-5">
                                                                      <div className="flex flex-col gap-4 px-0">
                                                                        <p className="text-[#132F40] text-md font-[500]">
                                                                          {
                                                                            item
                                                                              ?.wine_stock
                                                                              ?.wine
                                                                              ?.name
                                                                          }{" "}
                                                                          -{" "}
                                                                          {
                                                                            item
                                                                              ?.wine_stock
                                                                              ?.wine
                                                                              ?.product_type
                                                                          }
                                                                        </p>
                                                                        <div className="gap-1">
                                                                          <p className="text-[#505050] poppins-medium-italic text-xs">
                                                                            {
                                                                              item
                                                                                ?.wine_stock
                                                                                ?.wine
                                                                                ?.region
                                                                            }
                                                                          </p>
                                                                          <p className="text-[#505050] poppins-medium-italic text-xs">
                                                                            {
                                                                              item
                                                                                ?.wine_stock
                                                                                ?.wine
                                                                                ?.nation
                                                                            }
                                                                          </p>
                                                                          <p className="text-[#505050] poppins-medium-italic text-xs">
                                                                            {
                                                                              item
                                                                                ?.wine_stock
                                                                                ?.wine
                                                                                ?.grape_variety
                                                                            }
                                                                          </p>
                                                                          {item
                                                                            ?.wine_stock
                                                                            ?.wine
                                                                            ?.short_description && (
                                                                              <p
                                                                                className="text-[#505050] poppins-light-italic text-xs mt-3"
                                                                                dangerouslySetInnerHTML={{
                                                                                  __html:
                                                                                    item
                                                                                      ?.wine_stock
                                                                                      ?.wine
                                                                                      ?.short_description,
                                                                                }}
                                                                              />
                                                                            )}
                                                                        </div>
                                                                      </div>
                                                                    </div>
                                                                  </div>
                                                                )
                                                              )}
                                                          </div>
                                                        </div>
                                                      )}
                                                    </div>
                                                  )
                                                );
                                              })()}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </motion.div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  );
                });
              })()}
            </>
          )}
        </AnimatePresence>

        {/* {showMore && <div className="w-full flex justify-center mt-7">
                    <button
                        className="bg-[#8B2433] text-[#FFFFFF] flex justify-center items-center px-4 py-2 gap-3 rounded-full"
                        onClick={() => {
                            setShowMore(false);
                            setNumberOfProducts(60);
                        }}
                    >
                        VISUALIZZA TUTTO <FaLongArrowAltRightl />
                    </button>
                </div>} */}

        {/* Back Button */}
        {!isLoading.main && (
          <motion.div
            className="w-full flex justify-center mt-7"
            variants={itemVariants}
          >
            {["customer", "admin", "restaurant", null].includes(userType) && (
              <motion.button
                className="flex items-center px-2 py-1 bg-[#8A2533] text-white rounded-lg border-2 border-[#953543] hover:text-[#8A2533] hover:bg-[#f6efef] transition-colors group"
                onClick={() => navigate(-1)}
                {...buttonAnimations}
              >
                <IoIosArrowBack
                  size={"25px"}
                  color="white"
                  className="group-hover:invert"
                />
                <span className="ml-2 poppins-regular">{t('landingPage.backButton')}</span>
              </motion.button>
            )}
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
};

MenuProduct.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  menuData: PropTypes.shape({
    menu: PropTypes.shape({
      name: PropTypes.string,
      restaurant: PropTypes.shape({
        show_more: PropTypes.string,
      }),
    }),
    dishes_dishCourse: PropTypes.array,
  }).isRequired,
};

export default MenuProduct;
